import { useMemo, useState } from 'react';

import { dayRows, itemsOn, startOfWeek, todayPersonal, type CalItem } from '@/lib/calendarItems';

import { useCalendarItems } from './useCalendarItems';

/**
 * Неделя ученика на главной: семь дней, события по дням и строки выбранного дня.
 *
 * Список событий — тот же, что рисует страница расписания (`useCalendarItems`): события платформы, личные
 * записи и живые уроки. Телефонная главная и карточки широкого экрана считают неделю здесь, а не каждая сама.
 */
export function useWeekEvents() {
  const today = new Date();
  // Неделя начинается с понедельника, как в макете.
  const start = startOfWeek(today);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    }),
    [start.getTime()],
  );

  // Календарь есть и в приложении (сборка 1.1.1, решение владельца 02.10.2026) — неделя считается везде.
  // ⚠️ Условия «есть хотя бы один поток» здесь больше нет: оно глушило запрос у всех, кто ещё не зачислен, и
  // главная показывала пустую неделю вместо личных записей и событий «для всех». Охват считает сервер.
  const { items, isLoading } = useCalendarItems();

  const eventsOf = (d: Date): CalItem[] => itemsOn(items, d);

  // Выбранный день. Нажатие выбирает день здесь же, а не уводит в расписание:
  // решение заказчика от 24.08.2026 по мобильному экрану, тот же смысл.
  const [picked, setPicked] = useState<Date | null>(null);
  const shownDay = picked ?? today;

  // Строки выбранного дня: впереди идущие первыми, начавшиеся — следом и приглушённо (`lib/calendarItems.ts`).
  const { rows, fallback } = dayRows(items, shownDay, !!picked, today);

  /**
   * ⚠️ ПОКАЗЫВАЕМ ДО ТРЁХ, А НЕ ОДНО. Заказчик 07.09.2026: «календарь дисплеит
   * только 1 задачку, давай сделаем хотя бы 3, потому что 1 неприкольно».
   */
  const upcomingList = rows.slice(0, 3);

  return {
    today,
    days,
    eventsOf,
    picked,
    setPicked,
    shownDay,
    /** Все строки выбранного дня (или ближайшие вообще, если `fallback`). */
    rows,
    fallback,
    upcomingList,
    /** Личные записи на сегодня — для блока «Задачи на сегодня». */
    personalToday: todayPersonal(items, today),
    isLoading,
  };
}
